import React, { useState } from "react";
import { LicenseUpgradeService } from "../../utils/Services";
import { UpgradeLicenseContactFormRequest} from "../../models/UpgradeLicenseModel";
import { FormGroup, ControlLabel, FormControl } from "react-bootstrap";
import Select from "react-select";

interface UpgradeContactForm {
    license: string;
    onSuccess: (email: string) => void;
    onError: () => void;
}

const jobTitles = ["CEO", "CTO/CIO", "System Architect", "Director of IT", "Software Developer", "Other"].map(t => ({ value: t, label: t }));
const countries =
    "Afghanistan|Albania|Algeria|American Samoa|Andorra|Angola|Anguilla|Antarctica|Antigua and Barbuda|Argentina|Armenia|Aruban|Australia|Austria|Azerbaijan|Bahamas|Bahrain|Bangladesh|Barbados|Belarus|Belgium|Belize|Benin|Bermuda|Bhutan|Bolivia|Bosnia and Herzegovina|Botswana|Bouvet Island|Brazil|British Indian Ocean Territory|British Virgin Islands|Brunei|Bulgaria|Burkina Faso|Burundi|Cambodia|Cameroon|Canada|Cape Verde|Cayman Islands|Central African Republic|Chad|Chile|China|Christmas Island|Cocos Islands|Colombia|Comoros|Republic of the Congo|Cook Islands|Costa Rica|Croatia|Cuba|Cyprus|Czech Republic|Cote d'Ivoire|Denmark|Djibouti|Dominica|Dominican Republic|Ecuador|Egypt|El Salvador|Equatorial Guinea|Eritrea|Estonia|Ethiopia|Falkland Islands|Faroe Islands|Fiji|Finland|France|French Guiana|French Polynesia|French Southern Territories|Gabon|Gambia|Georgia|Germany|Ghana|Gibraltar|Greece|Greenland|Grenada|Guadeloupe|Guam|Guatemala|Guernsey|Guinea|GuineaBissau|Guyana|Haiti|Heard Island and McDonald Islands|Honduras|Hong Kong|Hungary|Iceland|India|Indonesia|Iran|Iraq|Ireland|Israel|Italy|Jamaica|Japan|Jersey|Jordan|Kazakhstan|Kenya|Kiribati|Kosovo|Kuwait|Kyrgyzstan|Laos|Latvia|Lebanon|Lesotho|Liberia|Libya|Liechtenstein|Lithuania|Luxembourg|Macao|North Macedonia|Madagascar|Malawi|Malaysia|Maldives|Mali|Malta|Marshall Islands|Martinique|Mauritania|Mauritius|Mayotte|Mexico|Micronesia|Moldova|Monaco|Mongolia|Montenegro|Montserrat|Morocco|Mozambique|Myanmar|Namibia|Nauru|Nepal|Netherlands|Netherlands Antilles|New Caledonia|New Zealand|Nicaragua|Niger|Nigeria|Niue|Norfolk Island|North Korea|Northern Ireland|Northern Mariana Islands|Norway|Oman|Pakistan|Palau|Palestine|Panama|Papua New Guinea|Paraguay|Peru|Philippines|Pitcairn|Poland|Portugal|Puerto Rico|Qatar|Reunion|Romania|Russia|Rwanda|Saint Helena|Saint Kitts and Nevis|Saint Lucia|Saint Pierre and Miquelon|Saint Vincent and the Grenadines|Samoa|San Marino|Sao Tome and Principe|Saudi Arabia|Senegal|Serbia|Serbia and Montenegro|Seychelles|Sierra Leone|Singapore|Slovakia|Slovenia|Solomon Islands|Somalia|South Africa|South Georgia and the South Sandwich Islands|South Korea|Spain|Sri Lanka|Sudan|Suriname|Svalbard and Jan Mayen|Eswatini|Sweden|Switzerland|Syria|Taiwan|Tajikistan|Tanzania|Thailand|Democratic Republic of the Congo|Timor-Leste|Togo|Tokelau|Tonga|Trinidad and Tobago|Tunisia|Turkey|Turkmenistan|Turks and Caicos Islands|Tuvalu|Virgin Islands|Uganda|Ukraine|United Arab Emirates|United Kingdom|United States|United States Minor Outlying Islands|Uruguay|Uzbekistan|Vanuatu|Vatican|Venezuela|Vietnam|Wallis and Futuna|Western Sahara|Yemen|Zambia|Zimbabwe|Aland Islands".split(
        "|"
    ).map(c => ({ value: c, label: c }));

const licenseUpgradeService = new LicenseUpgradeService();

export function UpgradeContactForm(props: UpgradeContactForm) {
    const { license, onSuccess, onError } = props;
    const [email, setEmail] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [jobTitle, setJobTitle] = useState<string>("");
    const [country, setCountry] = useState<string>("");
    
    const onSubmit = async (e: any) => {
        e.preventDefault();

        const dto: UpgradeLicenseContactFormRequest = {
            email,
            firstName,
            lastName,
            phone,
            jobTitle,
            country,
            license,
        };

        const response = await licenseUpgradeService.contactForm(dto);
        
        if (response.status === "Success") {
            onSuccess(email);
        } else if (response.status === "Invalid") {
            onError();
        }
    };
    
    
    return (
        <form id="upgradeContactForm" method="post" onSubmit={onSubmit} className="vstack gap-3 gap-md-4">
            <div className="row">
                <FormGroup controlId="email" className="col-12 mb-2 mb-md-3">
                    <ControlLabel className="mb-1">E-mail</ControlLabel>
                    <FormControl
                        type="email"
                        name="email"
                        required
                        value={email}
                        placeholder="e.g. name@company.com"
                        onChange={(e: any) => setEmail(e.target.value)}
                    />
                </FormGroup>

                <FormGroup controlId="firstName" className="col-sm-6 mb-2 mb-md-3">
                    <ControlLabel className="mb-1">First name</ControlLabel>
                    <FormControl
                        name="firstName"
                        required
                        value={firstName}
                        placeholder="Your first name"
                        onChange={(e: any) => setFirstName(e.target.value)}
                    />
                </FormGroup>

                <FormGroup controlId="lastName" className="col-sm-6 mb-2 mb-md-3">
                    <ControlLabel className="mb-1">Last name</ControlLabel>
                    <FormControl
                        name="lastName"
                        required
                        value={lastName}
                        placeholder="Your last name"
                        onChange={(e: any) => setLastName(e.target.value)}
                    />
                </FormGroup>

                <FormGroup controlId="phone" className="col-sm-6 mb-2 mb-md-3">
                    <ControlLabel className="mb-1">Phone (optional)</ControlLabel>
                    <FormControl
                        name="phone"
                        value={phone}
                        placeholder="Your phone number"
                        onChange={(e: any) => setPhone(e.target.value)}
                    />
                </FormGroup>

                <FormGroup controlId="jobTitle" className="col-sm-6 mb-2 mb-md-3">
                    <ControlLabel className="mb-1">Job title</ControlLabel>
                    <Select
                        id="jobTitle"
                        options={jobTitles}
                        value={jobTitles.find(opt => opt.value === jobTitle)}
                        onChange={(val: any) => setJobTitle(val ? val.value : "")}
                        placeholder="Select a job title"
                        classNamePrefix="rdb-select"
                        menuPortalTarget={document.body}
                        menuPortalClassName="rdb-select-portal"
                        menuPosition="fixed"
                    />
                </FormGroup>

                <FormGroup controlId="country" className="col-12 mb-2 mb-md-3">
                    <ControlLabel className="mb-1">Country</ControlLabel>
                    <Select
                        id="country"
                        options={countries}
                        value={countries.find(opt => opt.value === country)}
                        onChange={(val: any) => setCountry(val ? val.value : "")}
                        placeholder="Your country"
                        classNamePrefix="rdb-select"
                        menuPortalTarget={document.body}
                        menuPortalClassName="rdb-select-portal"
                        menuPosition="fixed"
                    />
                </FormGroup>
            </div>
        </form>
    );
}
